import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Heuristics = () => {
  const { selectedProject } = useApp();
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', related_url: '' });

  useEffect(() => {
    if (selectedProject) {
      fetchEvaluations();
    }
  }, [selectedProject]);

  const fetchEvaluations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/heuristics/evaluations?project_id=${selectedProject.id}`);
      setEvaluations(response.data);
    } catch (error) {
      toast.error('Erro ao carregar avaliações');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/api/heuristics/evaluations`, {
        ...formData,
        project_id: selectedProject.id
      });
      toast.success('Avaliação criada com sucesso!');
      setDialogOpen(false);
      setFormData({ title: '', description: '', related_url: '' });
      navigate(`/heuristics/${response.data.id}`);
    } catch (error) {
      toast.error('Erro ao criar avaliação');
    }
  };

  if (!selectedProject) {
    return <div className="text-center py-12">Selecione um projeto</div>;
  }

  if (loading) {
    return <div className="flex justify-center"><div className="animate-spin h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  const statusColors = {
    draft: 'bg-gray-500',
    in_progress: 'bg-blue-500',
    finished: 'bg-green-500'
  };

  const statusLabels = {
    draft: 'Rascunho',
    in_progress: 'Em Andamento',
    finished: 'Finalizada'
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold" >Avaliações Heurísticas</h1>
          <p className="text-sm" >Gerencie as avaliações heurísticas do projeto</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="mr-2 h-4 w-4" />Nova Avaliação</Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>Criar Nova Avaliação</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Título</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Descrição</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>URL Relacionada</Label>
                  <Input value={formData.related_url} onChange={(e) => setFormData({...formData, related_url: e.target.value})} placeholder="https://..." />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit">Criar Avaliação</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {evaluations.map((evaluation) => (
          <Card key={evaluation.id} className="hover-card cursor-pointer"  onClick={() => navigate(`/heuristics/${evaluation.id}`)}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg" >{evaluation.title}</CardTitle>
                <Badge className={statusColors[evaluation.status]}>{statusLabels[evaluation.status]}</Badge>
              </div>
              <CardDescription >{evaluation.description || 'Sem descrição'}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-sm" >
                Criado em {new Date(evaluation.created_at).toLocaleDateString('pt-BR')}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
